import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2, Trash2, Sparkles } from "lucide-react";

const SESSION_KEY = "pk_ai_chat_session";
const SUGGESTIONS = [
  "How do memberships work?",
  "How do I start a Friends Only challenge?",
  "How do I invite my friends?",
  "How is the top 5 scored?",
];

const newSessionId = () => `chat_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

export const SupportChat = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(SESSION_KEY, sid);
    }
    setSessionId(sid);
  }, []);

  useEffect(() => {
    if (!open || !sessionId) return;
    api
      .get(`/ai/chat/${sessionId}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, streaming]);

  if (!user) return null;

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: question }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Chat unavailable");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: e.message || "Something went wrong. Reach a human at /contact.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clear = async () => {
    await api.delete(`/ai/chat/${sessionId}`).catch(() => {});
    setMessages([]);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-chat-fab"
          aria-label="Ask the assistant"
          className="fixed right-4 md:right-6 z-[55] w-14 h-14 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-2xl flex items-center justify-center"
          style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div
          className="fixed z-[60] inset-x-3 md:inset-x-auto md:right-6 md:w-[380px] border border-white/15 bg-[#0f0f0f] shadow-2xl flex flex-col"
          style={{
            bottom: "calc(5rem + env(safe-area-inset-bottom))",
            maxHeight: "min(70vh, 560px)",
          }}
          data-testid="support-chat-panel"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10">
            <div className="inline-flex items-center gap-2 min-w-0">
              <Sparkles size={14} className="text-[#C4B5FD] flex-shrink-0" />
              <span className="font-display text-lg tracking-wide truncate">ASK PICKEMSPORTS</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={clear}
                data-testid="support-chat-clear"
                className="p-1.5 text-white/40 hover:text-white"
                aria-label="Clear conversation"
              >
                <Trash2 size={15} />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="support-chat-close"
                className="p-1.5 text-white/40 hover:text-white"
                aria-label="Close chat"
              >
                <X size={17} />
              </button>
            </div>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-chat-messages">
            {messages.length === 0 && (
              <>
                <p className="text-white/50 text-sm">
                  Ask about memberships, challenges, invites or scoring — I answer from the actual house rules.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`support-chat-suggestion-${s.split(" ")[2] || s.split(" ")[1]}`}
                      className="border border-white/15 hover:border-[#7C3AED] text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-full"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`text-sm leading-relaxed ${
                  m.role === "user" ? "text-right" : ""
                }`}
              >
                <span
                  className={`inline-block px-3 py-2 rounded-sm max-w-[88%] whitespace-pre-wrap text-left ${
                    m.role === "user"
                      ? "bg-white/10 text-white"
                      : "bg-[#7C3AED]/15 border border-[#7C3AED]/25 text-white/85"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "..." : "")}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 p-2.5">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask a question..."
                data-testid="support-chat-input"
                className="flex-1 bg-[#141414] border border-white/10 focus:border-[#7C3AED] rounded-sm px-3 py-2.5 text-sm outline-none min-w-0"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="support-chat-send"
                className="bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 text-white p-2.5 rounded-sm flex-shrink-0"
                aria-label="Send"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="text-white/25 text-[10px] mt-1.5 px-0.5">
              AI assistant — can make mistakes. For anything account-specific, use /contact.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
